import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"
import { FlaskConical } from "lucide-react"
import { formatGigDate, type GigDatePrecision } from "@/lib/gigDates"

type GigRow = {
  id: number
  gig_date: string | null
  gig_date_precision: GigDatePrecision
  artist_name: string | null
  artist_type: string | null
  venue_name: string | null
  venue_state: string | null
  venue_city: string | null
}

type Region = { id: number; name: string }

type Filters = {
  year?: string
  state?: string
  region_id?: string
  artist_type?: string
  venue?: string
}

type Props = {
  gigs: GigRow[]
  total_count: number
  capped: boolean
  states: string[]
  regions: Region[]
  filters: Filters
  sort: string
  dir: "asc" | "desc"
}

const ENTITY_TABS = [
  { label: "Recordings",      href: "/admin/research/recordings" },
  { label: "People",          href: "/admin/research/people" },
  { label: "Gigs",            href: "/admin/research/gigs" },
  { label: "Chart Listings",  href: "/admin/research/chart_listings" },
]

// Research rows aren't grouped by year, so the year is added back when known.
function formatResearchDate(iso: string | null, precision: GigDatePrecision): string {
  if (!iso) return "—"
  const year = iso.slice(0, 4)
  if (precision === "year")      return year
  if (precision === "day_month") return `${formatGigDate(iso, null, precision)}, year unknown`
  return `${formatGigDate(iso, null, precision)} ${year}`
}

const ic = "w-full rounded border border-hairline bg-page px-3 py-1.5 text-sm text-ink-body placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-current"
const sc = "w-full rounded border border-hairline bg-page px-3 py-1.5 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"

export default function ResearchGigs({ gigs, total_count, capped, states, regions, filters, sort, dir }: Props) {
  const [form, setForm] = useState<Filters>(filters)

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  function buildAllParams(overrides: Record<string, string> = {}): Record<string, string> {
    const p: Record<string, string> = {}
    Object.entries(form).forEach(([k, v]) => { if (v) p[k] = v })
    if (sort && sort !== "gig_date") p.sort = sort
    if (dir && dir !== "desc") p.dir = dir
    return { ...p, ...overrides }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    router.get("/admin/research/gigs", buildAllParams(), { preserveState: false })
  }

  function handleReset() {
    setForm({})
    router.get("/admin/research/gigs", {}, { preserveState: false })
  }

  function handleSort(col: string) {
    const newDir = sort === col && dir === "desc" ? "asc" : "desc"
    router.get("/admin/research/gigs", buildAllParams({ sort: col, dir: newDir }), { preserveState: false })
  }

  function SortHeader({ col, children }: { col: string; children: React.ReactNode }) {
    const active = sort === col
    return (
      <th className="pb-2 pr-4 font-medium">
        <button onClick={() => handleSort(col)} className="flex items-center gap-1 hover:text-ink-body transition-colors whitespace-nowrap">
          {children}
          <span className={active ? "text-ink-body" : "opacity-30"}>
            {active ? (dir === "asc" ? "↑" : "↓") : "↕"}
          </span>
        </button>
      </th>
    )
  }

  const isFiltering = Object.values(filters).some(Boolean)

  return (
    <>
      <Head title="Research: Gigs">
        <meta name="description" content="Filter and search gigs by field." />
        <meta property="og:title" content="Research: Gigs" />
        <meta property="og:description" content="Filter and search gigs by field." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex items-center gap-2 mb-1">
            <FlaskConical className="h-5 w-5 text-ink-muted" />
            <h1>Research</h1>
          </div>
          <div className="flex gap-4 mt-3 border-b border-hairline -mx-6 sm:-mx-10 px-6 sm:px-10">
            {ENTITY_TABS.map((tab) => (
              <Link
                key={tab.href}
                href={tab.href}
                className={[
                  "pb-3 text-sm font-medium no-underline border-b-2 -mb-px transition-colors",
                  tab.label === "Gigs"
                    ? "border-current text-ink-body"
                    : "border-transparent text-ink-muted hover:text-ink-body"
                ].join(" ")}
              >
                {tab.label}
              </Link>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Year</label>
            <input name="year" type="number" value={form.year ?? ""} onChange={handleChange} placeholder="e.g. 1987" className={ic} min={1900} max={2100} />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">State</label>
            <select name="state" value={form.state ?? ""} onChange={handleChange} className={sc}>
              <option value="">All states</option>
              {states.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Region</label>
            <select name="region_id" value={form.region_id ?? ""} onChange={handleChange} className={sc}>
              <option value="">All regions</option>
              {regions.map((r) => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Artist type</label>
            <select name="artist_type" value={form.artist_type ?? ""} onChange={handleChange} className={sc}>
              <option value="">All</option>
              <option value="Band">Band</option>
              <option value="Person">Person</option>
            </select>
          </div>
          <div className="col-span-2 sm:col-span-4">
            <label className="block text-xs font-medium text-ink-muted mb-1">Venue name</label>
            <input name="venue" type="text" value={form.venue ?? ""} onChange={handleChange} placeholder="e.g. The Tote" className={ic} />
          </div>
          <div className="col-span-2 sm:col-span-4 flex items-center gap-3 pt-1">
            <Button type="submit">Search</Button>
            {isFiltering && (
              <button type="button" onClick={handleReset} className="text-sm text-ink-muted hover:text-ink-body transition-colors">
                Clear filters
              </button>
            )}
          </div>
        </form>

        {isFiltering && (
          <p className="mt-4 text-sm text-ink-muted">
            {capped
              ? `Showing first 200 of ${total_count} results`
              : `${total_count} ${total_count === 1 ? "result" : "results"}`}
          </p>
        )}

        {isFiltering && gigs.length === 0 ? (
          <p className="mt-8 text-center text-sm text-ink-muted">No gigs match your filters.</p>
        ) : gigs.length > 0 ? (
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline text-left text-xs text-ink-muted">
                  <SortHeader col="gig_date">Date</SortHeader>
                  <th className="pb-2 pr-4 font-medium">Artist</th>
                  <SortHeader col="venue_name">Venue</SortHeader>
                  <SortHeader col="venue_city">City</SortHeader>
                  <SortHeader col="venue_state">State</SortHeader>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {gigs.map((g) => (
                  <tr key={g.id} className="hover:bg-surface">
                    <td className="py-2 pr-4 text-ink-muted tabular-nums whitespace-nowrap">
                      {formatResearchDate(g.gig_date, g.gig_date_precision)}
                    </td>
                    <td className="py-2 pr-4">
                      <span className="font-medium text-ink-body">{g.artist_name ?? "—"}</span>
                      {g.artist_type && (
                        <span className="ml-1.5 text-xs text-ink-muted">{g.artist_type}</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-ink-muted">{g.venue_name ?? "—"}</td>
                    <td className="py-2 pr-4 text-ink-muted">{g.venue_city ?? "—"}</td>
                    <td className="py-2 text-ink-muted">{g.venue_state ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </AdminShell>
    </>
  )
}
